import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";

const statuses = [
  { value: "planning", label: "Planlegger" },
  { value: "active", label: "Aktiv" },
  { value: "on_hold", label: "På Vent" },
  { value: "completed", label: "Fullført" },
  { value: "cancelled", label: "Kansellert" },
];

const getDisplayName = (user) => {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user?.full_name || user?.email || "";
};

export default function ProjectForm({ project, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "planning",
    start_date: "",
    end_date: "",
    owner: "",
    related_to_type: "none",
    related_to_id: "",
    budget: "",
    hourly_rate: 2500,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const { data: investors = [] } = useQuery({
    queryKey: ["investors"],
    queryFn: () => base44.entities.Investor.list(),
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: () => base44.entities.BusinessCustomer.list(),
  });

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || "",
        description: project.description || "",
        status: project.status || "planning",
        start_date: project.start_date || "",
        end_date: project.end_date || "",
        owner: project.owner || "",
        related_to_type: project.related_to_type || "none",
        related_to_id: project.related_to_id || "",
        budget: project.budget || "",
        hourly_rate: project.hourly_rate || 2500,
      });
    }
  }, [project]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const owner = users.find(u => u.email === formData.owner);
    
    let relatedName = null;
    if (formData.related_to_type === "investor" && formData.related_to_id) {
      const investor = investors.find(i => i.id === formData.related_to_id);
      relatedName = investor?.name;
    } else if (formData.related_to_type === "customer" && formData.related_to_id) {
      const customer = customers.find(c => c.id === formData.related_to_id);
      relatedName = customer?.company_name;
    }

    const data = {
      ...formData,
      owner_name: owner ? getDisplayName(owner) : formData.owner,
      related_to_id: formData.related_to_type !== "none" && formData.related_to_type !== "internal" ? formData.related_to_id : null,
      related_to_name: relatedName,
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {project ? "Rediger Prosjekt" : "Opprett Prosjekt"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="name">Prosjektnavn *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="h-11"
              placeholder="Navn på prosjektet"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Beskriv prosjektet..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="owner">Prosjekteier *</Label>
              <Select value={formData.owner} onValueChange={(v) => setFormData({ ...formData, owner: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Velg eier" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.email}>
                      {getDisplayName(user)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="start_date">Startdato</Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="h-11"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="end_date">Sluttdato</Label>
              <Input
                id="end_date"
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="h-11"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="related_to_type">Relatert Til</Label>
              <Select 
                value={formData.related_to_type} 
                onValueChange={(v) => setFormData({ ...formData, related_to_type: v, related_to_id: "" })}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Ingen</SelectItem>
                  <SelectItem value="internal">Internt</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                  <SelectItem value="customer">Kunde</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.related_to_type === "investor" && (
              <div className="space-y-2">
                <Label htmlFor="related_to_id">Velg Investor</Label>
                <Select 
                  value={formData.related_to_id} 
                  onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Velg investor" />
                  </SelectTrigger>
                  <SelectContent>
                    {investors.map((investor) => (
                      <SelectItem key={investor.id} value={investor.id}>
                        {investor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.related_to_type === "customer" && (
              <div className="space-y-2">
                <Label htmlFor="related_to_id">Velg Kunde</Label>
                <Select 
                  value={formData.related_to_id} 
                  onValueChange={(v) => setFormData({ ...formData, related_to_id: v })}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Velg kunde" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="budget">Budsjett (NOK)</Label>
              <Input
                id="budget"
                type="number"
                min="0"
                step="1000"
                value={formData.budget}
                onChange={(e) => setFormData({ ...formData, budget: e.target.value ? parseFloat(e.target.value) : "" })}
                className="h-11"
                placeholder="f.eks. 500000"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="hourly_rate">Timepris (NOK)</Label>
              <Input
                id="hourly_rate"
                type="number"
                min="0"
                step="100"
                value={formData.hourly_rate}
                onChange={(e) => setFormData({ ...formData, hourly_rate: e.target.value ? parseFloat(e.target.value) : "" })}
                className="h-11"
                placeholder="f.eks. 2500"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Lagrer..." : project ? "Oppdater" : "Opprett"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}